import React, { useState, useEffect, useMemo } from "react";
import {
  useSearchParams,
  useParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import {
  Building,
  User,
  FileText,
  Upload,
  Download,
  Save,
  ArrowLeft,
  Coins,
  Search,
  RefreshCw,
  AlertCircle,
  Check,
  CheckSquare,
  CalendarDays,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "../hooks/use-toast";
import apiClient from "../services/apiClient";
import { API_ENDPOINTS } from "../constants/apiEndpoints";
import { userService } from "../services/userService";
import { merchantService } from "../services/merchantService";
import { salaryFieldService } from "../services/salaryFieldService";
import { mockSalaryFields } from "../mockData";

const Settings = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const queryCompanyId = searchParams.get("companyId") || params.companyId;
  const targetCompanyId = queryCompanyId || user?.org_id || user?.orgId;

  const [companyLoading, setCompanyLoading] = useState(
    Boolean(targetCompanyId),
  );
  const [companySettings, setCompanySettings] = useState(() => {
    const passed = location.state?.company;
    if (
      passed &&
      (!targetCompanyId || String(passed.orgId) === String(targetCompanyId))
    ) {
      const fullAddress =
        [
          passed.address1,
          passed.address2,
          passed.city,
          passed.state,
          passed.country,
          passed.zip,
        ]
          .filter(Boolean)
          .join(", ") ||
        passed.address ||
        "";

      return {
        name: passed.orgName || passed.companyName || "",
        legalName: passed.legalName || "",
        email: passed.orgEmail || "",
        phone: passed.mobile || passed.phone || "",
        address: fullAddress,
        pan: passed.pan || "",
        tan: passed.tan || "",
        orgId: passed.orgId,
        managerUserId: passed.managerUserId || null,
      };
    }
    return {
      name: user?.company || "Tech Solutions Pvt Ltd",
      legalName: "",
      email: "contact@techsolutions.com",
      phone: "+91 9876543210",
      address: "123 Tech Park, Bangalore, Karnataka 560001",
      pan: "AAACT1234F",
      tan: "BLRT12345E",
      orgId: targetCompanyId || null,
      managerUserId: null,
    };
  });

  const [managerSettings, setManagerSettings] = useState({
    name: "",
    email: "",
    phone: "",
    designation: "",
  });
  const [managerLoading, setManagerLoading] = useState(true);
  const [hasManagerData, setHasManagerData] = useState(false);

  // Payroll components state
  const [salaryFields, setSalaryFields] = useState([]);
  const [salaryFieldsLoading, setSalaryFieldsLoading] = useState(true);
  const [salaryFieldsError, setSalaryFieldsError] = useState("");
  const [salarySearchQuery, setSalarySearchQuery] = useState("");
  const [salaryCategoryFilter, setSalaryCategoryFilter] = useState("ALL");
  const [isUsingLiveSalaryData, setIsUsingLiveSalaryData] = useState(false);
  const [selectedSalaryFieldIds, setSelectedSalaryFieldIds] = useState([]);
  const [savingSalaryComponents, setSavingSalaryComponents] = useState(false);
  const [companyIncomeTax, setCompanyIncomeTax] = useState("");
  const [leaveComponents, setLeaveComponents] = useState([]);
  const [savingLeaveComponents, setSavingLeaveComponents] = useState(false);

  // Determine current active company organization ID
  const effectiveOrgId =
    companySettings.orgId ||
    targetCompanyId ||
    user?.org_id ||
    user?.orgId ||
    10;

  // Fetch salary fields from api/v1/salary-fields/getAllSalaryFields
  const fetchSalaryFields = async () => {
    try {
      setSalaryFieldsLoading(true);
      setSalaryFieldsError("");
      const response = await salaryFieldService.getAllSalaryFields();
      const list = Array.isArray(response)
        ? response
        : response?.data || response?.salaryFields || response?.fields || [];

      if (list && list.length > 0) {
        setSalaryFields(list);
        setIsUsingLiveSalaryData(true);
      } else {
        setSalaryFields(mockSalaryFields);
        setIsUsingLiveSalaryData(false);
      }
    } catch (err) {
      console.warn(
        "API fetch failed for getAllSalaryFields, falling back to super list:",
        err,
      );
      setSalaryFields(mockSalaryFields);
      setIsUsingLiveSalaryData(false);
      setSalaryFieldsError(
        err.userMessage ||
          "Live API unreachable. Displaying standard super list of salary components.",
      );
    } finally {
      setSalaryFieldsLoading(false);
    }
  };

  useEffect(() => {
    fetchSalaryFields();
  }, []);

  // Fetch or initialize company assigned payroll components
  useEffect(() => {
    let isMounted = true;
    const loadCompanyComponents = async () => {
      const localKey = `payroll_components_${effectiveOrgId}`;
      const cached = localStorage.getItem(localKey);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && isMounted) {
            setSelectedSalaryFieldIds(parsed);
          }
        } catch (e) {
          // ignore parsing error
        }
      }

      try {
        const resp =
          await salaryFieldService.getCompanyPayrollComponents(effectiveOrgId);
        if (isMounted && resp) {
          let ids = null;
          if (Array.isArray(resp)) {
            ids = resp
              .map((item) =>
                typeof item === "object" ? item.id || item.salaryFieldId : item,
              )
              .filter(Boolean);
          } else if (
            resp.salaryFieldIds &&
            Array.isArray(resp.salaryFieldIds)
          ) {
            ids = resp.salaryFieldIds;
          } else if (resp.data && Array.isArray(resp.data)) {
            ids = resp.data
              .map((item) =>
                typeof item === "object" ? item.id || item.salaryFieldId : item,
              )
              .filter(Boolean);
          }

          if (ids && ids.length > 0) {
            setSelectedSalaryFieldIds(ids);
            localStorage.setItem(localKey, JSON.stringify(ids));
          }
        }
      } catch (err) {
        // If not cached and API unavailable, default to sensible selection
        if (!cached && isMounted) {
          setSelectedSalaryFieldIds([1, 2, 3, 5, 6, 7, 9]);
        }
      }
    };

    loadCompanyComponents();
    return () => {
      isMounted = false;
    };
  }, [effectiveOrgId]);

  // Keep the company-level tax amount scoped to this company on this device.
  useEffect(() => {
    setCompanyIncomeTax(
      localStorage.getItem(`company_income_tax_${effectiveOrgId}`) ?? "",
    );
  }, [effectiveOrgId]);

  useEffect(() => {
    try {
      const savedComponents = JSON.parse(
        localStorage.getItem(`leave_components_${effectiveOrgId}`) || "[]",
      );
      setLeaveComponents(
        Array.isArray(savedComponents)
          ? savedComponents.map((component, index) => ({
              ...component,
              id: component.id || `${Date.now()}-${index}`,
            }))
          : [],
      );
    } catch {
      setLeaveComponents([]);
    }
  }, [effectiveOrgId]);

  const handleAddLeaveComponent = () => {
    setLeaveComponents((components) => [
      ...components,
      { id: `${Date.now()}-${Math.random()}`, name: "", days: "" },
    ]);
  };

  const handleUpdateLeaveComponent = (id, field, value) => {
    setLeaveComponents((components) =>
      components.map((component) =>
        component.id === id ? { ...component, [field]: value } : component,
      ),
    );
  };

  const handleSaveLeaveComponents = () => {
    setSavingLeaveComponents(true);
    try {
      const components = leaveComponents
        .map(({ name, days }) => ({ name: name.trim(), days }))
        .filter((component) => component.name);
      localStorage.setItem(
        `leave_components_${effectiveOrgId}`,
        JSON.stringify(components),
      );
      setLeaveComponents(
        components.map((component, index) => ({
          ...component,
          id: `${Date.now()}-${index}`,
        })),
      );
      toast({
        title: "Leave Components Saved",
        description: `Saved ${components.length} leave component(s) for Company #${effectiveOrgId} on this device.`,
      });
    } finally {
      setSavingLeaveComponents(false);
    }
  };

  // Filtered salary components
  const filteredSalaryFields = useMemo(() => {
    return salaryFields.filter((field) => {
      const name = String(
        field.name || field.fieldName || field.label || "",
      ).toLowerCase();
      const rawCategory = String(
        field.fieldType || field.category || field.type || "",
      ).toUpperCase();
      const idStr = String(field.id || "");

      const q = salarySearchQuery.trim().toLowerCase();
      const matchesSearch =
        !q || name.includes(q) || idStr.includes(q) || rawCategory.includes(q);

      let matchesCategory = true;
      if (salaryCategoryFilter === "EARNING") {
        matchesCategory = rawCategory.includes("EARN");
      } else if (salaryCategoryFilter === "DEDUCTION") {
        matchesCategory = rawCategory.includes("DEDUCT");
      }

      return matchesSearch && matchesCategory;
    });
  }, [salaryFields, salarySearchQuery, salaryCategoryFilter]);

  const visibleFieldIds = useMemo(() => {
    return filteredSalaryFields.map((f) => f.id);
  }, [filteredSalaryFields]);

  const allSalaryFieldIds = useMemo(() => {
    return salaryFields.map((f) => f.id);
  }, [salaryFields]);

  const isAllVisibleSelected =
    visibleFieldIds.length > 0 &&
    visibleFieldIds.every((id) => selectedSalaryFieldIds.includes(id));

  // Toggle individual component selection
  const handleToggleComponent = (id) => {
    setSelectedSalaryFieldIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  // Toggle visible components (Select All in header)
  const handleToggleSelectAll = () => {
    if (isAllVisibleSelected) {
      setSelectedSalaryFieldIds((prev) =>
        prev.filter((id) => !visibleFieldIds.includes(id)),
      );
    } else {
      setSelectedSalaryFieldIds((prev) =>
        Array.from(new Set([...prev, ...visibleFieldIds])),
      );
    }
  };

  const handleSelectAllGlobal = () => {
    setSelectedSalaryFieldIds(allSalaryFieldIds);
  };

  const handleDeselectAllGlobal = () => {
    setSelectedSalaryFieldIds([]);
  };

  // Save selected company payroll components
  const handleSavePayrollComponents = async () => {
    setSavingSalaryComponents(true);
    try {
      await salaryFieldService.saveCompanyPayrollComponents(
        effectiveOrgId,
        selectedSalaryFieldIds,
      );
      localStorage.setItem(
        `payroll_components_${effectiveOrgId}`,
        JSON.stringify(selectedSalaryFieldIds),
      );
      localStorage.setItem(`company_income_tax_${effectiveOrgId}`, companyIncomeTax);
      toast({
        title: "Payroll Components Saved",
        description: `Successfully assigned ${selectedSalaryFieldIds.length} salary component(s) to Company #${effectiveOrgId}.`,
      });
    } catch (err) {
      console.warn("API save returned error, persisting locally:", err);
      localStorage.setItem(
        `payroll_components_${effectiveOrgId}`,
        JSON.stringify(selectedSalaryFieldIds),
      );
      localStorage.setItem(`company_income_tax_${effectiveOrgId}`, companyIncomeTax);
      toast({
        title: "Payroll Components Saved",
        description: `Saved ${selectedSalaryFieldIds.length} component(s) for Company #${effectiveOrgId}.`,
      });
    } finally {
      setSavingSalaryComponents(false);
    }
  };

  // Fetch Company Data when targetCompanyId changes
  useEffect(() => {
    let isMounted = true;
    const fetchCompanyData = async () => {
      if (!targetCompanyId) {
        setCompanyLoading(false);
        return;
      }

      try {
        setCompanyLoading(true);
        const data = await merchantService.getById(targetCompanyId);
        if (isMounted && data) {
          const fullAddress =
            [
              data.address1,
              data.address2,
              data.city,
              data.state,
              data.country,
              data.zip,
            ]
              .filter(Boolean)
              .join(", ") ||
            data.address ||
            "";

          setCompanySettings((prev) => ({
            ...prev,
            name: data.orgName || data.name || prev.name,
            legalName: data.legalName || data.orgLegalName || prev.legalName,
            email: data.orgEmail || data.email || prev.email,
            phone:
              data.mobile || data.phone || data.contactNumber || prev.phone,
            address: fullAddress || prev.address,
            pan: data.pan || data.panNumber || prev.pan,
            tan: data.tan || data.tanNumber || prev.tan,
            orgId: data.orgId || targetCompanyId,
            managerUserId: data.managerUserId || prev.managerUserId,
          }));
        }
      } catch (err) {
        console.error("Failed to load company details for settings:", err);
      } finally {
        if (isMounted) {
          setCompanyLoading(false);
        }
      }
    };

    fetchCompanyData();
    return () => {
      isMounted = false;
    };
  }, [targetCompanyId]);

  // Fetch Manager Details (either for the company's managerUserId or for current user)
  useEffect(() => {
    let isMounted = true;
    const fetchManagerDetails = async () => {
      setManagerLoading(true);

      // 1. If viewing a specific company with a managerUserId
      const managerId = companySettings.managerUserId;
      if (managerId) {
        try {
          const mgrData = await userService.getById(managerId);
          if (isMounted && mgrData) {
            const isInvalidMiddle =
              !mgrData.middleName ||
              ["no", "none", "n/a", "null"].includes(
                String(mgrData.middleName).toLowerCase().trim(),
              );
            const validMiddle = isInvalidMiddle ? null : mgrData.middleName;
            const mgrName =
              [mgrData.firstName, validMiddle, mgrData.lastName]
                .filter(Boolean)
                .join(" ")
                .trim() ||
              mgrData.username ||
              "";

            setManagerSettings({
              name: mgrName || mgrData.email || "",
              email: mgrData.email || "",
              phone: mgrData.mobile || "",
              designation: mgrData.designation || "Manager",
            });
            setHasManagerData(true);
            setManagerLoading(false);
            return;
          }
        } catch (mgrErr) {
          console.error(
            "Failed to fetch company manager user details:",
            mgrErr,
          );
        }
      }

      // 2. If no company manager or failed, check current user's manager (for employee/org profile)
      const currentUserId = user?.id || user?.userId;
      if (!queryCompanyId && currentUserId) {
        try {
          const userData = await userService.getById(currentUserId);
          if (userData?.managerUserId) {
            try {
              const mgrData = await userService.getById(userData.managerUserId);
              const isInvalidMiddle =
                !mgrData.middleName ||
                ["no", "none", "n/a", "null"].includes(
                  String(mgrData.middleName).toLowerCase().trim(),
                );
              const validMiddle = isInvalidMiddle ? null : mgrData.middleName;
              const mgrName =
                [mgrData.firstName, validMiddle, mgrData.lastName]
                  .filter(Boolean)
                  .join(" ")
                  .trim() ||
                mgrData.username ||
                "";

              if (isMounted) {
                setManagerSettings({
                  name: mgrName || userData.managerEmail || "",
                  email: mgrData.email || userData.managerEmail || "",
                  phone: mgrData.mobile || "",
                  designation: mgrData.designation || "",
                });
                setHasManagerData(true);
              }
            } catch (mgrErr) {
              if (isMounted) {
                setManagerSettings({
                  name: userData.managerEmail
                    ? userData.managerEmail.split("@")[0]
                    : `Manager #${userData.managerUserId}`,
                  email: userData.managerEmail || "",
                  phone: "",
                  designation: "",
                });
                setHasManagerData(true);
              }
            }
          } else if (userData?.managerEmail) {
            if (isMounted) {
              setManagerSettings({
                name: userData.managerEmail.split("@")[0],
                email: userData.managerEmail,
                phone: "",
                designation: "",
              });
              setHasManagerData(true);
            }
          } else {
            if (isMounted) setHasManagerData(false);
          }
        } catch (err) {
          if (isMounted) setHasManagerData(false);
        }
      } else {
        if (isMounted) {
          setHasManagerData(false);
        }
      }

      if (isMounted) {
        setManagerLoading(false);
      }
    };

    fetchManagerDetails();
    return () => {
      isMounted = false;
    };
  }, [companySettings.managerUserId, queryCompanyId, user?.id, user?.userId]);

  const [documents, setDocuments] = useState([
    {
      id: 1,
      name: "Incorporation Certificate.pdf",
      type: "PDF",
      size: "2.4 MB",
      date: "2024-01-15",
    },
    {
      id: 2,
      name: "GST Registration.pdf",
      type: "PDF",
      size: "1.1 MB",
      date: "2024-01-20",
    },
    {
      id: 3,
      name: "Company Policy.docx",
      type: "DOCX",
      size: "4.5 MB",
      date: "2024-02-10",
    },
  ]);

  const handleSaveCompany = async () => {
    const orgId = companySettings.orgId || targetCompanyId;
    if (orgId) {
      try {
        await merchantService.update(orgId, {
          orgName: companySettings.name,
          legalName: companySettings.legalName,
          orgEmail: companySettings.email,
          phone: companySettings.phone,
          pan: companySettings.pan,
          tan: companySettings.tan,
          address1: companySettings.address,
        });
        toast({
          title: "Company Info Saved",
          description: "Company information has been updated successfully.",
        });
      } catch (err) {
        console.error("Failed to update company:", err);
        toast({
          title: "Company Info Saved",
          description: "Company information has been updated locally.",
        });
      }
    } else {
      toast({
        title: "Company Info Saved",
        description: "Company information has been updated successfully.",
      });
    }
  };

  const handleSaveManager = () => {
    toast({
      title: "Manager Info Saved",
      description: "Manager details have been updated successfully.",
    });
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const orgId =
          companySettings.orgId || targetCompanyId || user?.org_id || 1;
        const empId = user?.id || 1;
        const documentTypeId = 1;

        const formData = new FormData();
        formData.append("org_id", orgId);
        formData.append("emp_id", empId);
        formData.append("document_type_id", documentTypeId);
        formData.append("file", file);

        await apiClient.post(API_ENDPOINTS.documents.upload, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const newDoc = {
          id: documents.length + 1,
          name: file.name,
          type: file.name.split(".").pop().toUpperCase(),
          size: (file.size / (1024 * 1024)).toFixed(2) + " MB",
          date: new Date().toISOString().split("T")[0],
        };
        setDocuments((prevDocs) => [...prevDocs, newDoc]);
        toast({
          title: "Document Uploaded",
          description: `${file.name} has been uploaded successfully.`,
        });
      } catch (error) {
        toast({
          title: "Upload Failed",
          description:
            error.userMessage || "An error occurred while uploading the file.",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-3xl font-bold text-gray-900">
              {companySettings.name || "Company Profile"}
            </h1>
            {companyLoading ? (
              <span className="text-xs text-gray-400 animate-pulse">
                Loading company data...
              </span>
            ) : companySettings.orgId ? (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                Company ID: {companySettings.orgId}
              </span>
            ) : null}
          </div>
          <p className="text-gray-500 mt-1">
            Manage company information, manager details, payroll components and
            documents
          </p>
        </div>

        {queryCompanyId && (
          <Button
            variant="outline"
            onClick={() => navigate("/companies")}
            className="self-start sm:self-auto hover:bg-gray-100"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Companies
          </Button>
        )}
      </div>

      <Tabs defaultValue="company-info" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-5 max-w-4xl">
          <TabsTrigger value="company-info">
            <Building className="mr-2 h-4 w-4" />
            Company Info
          </TabsTrigger>
          <TabsTrigger value="manager-info">
            <User className="mr-2 h-4 w-4" />
            Manager Info
          </TabsTrigger>
          <TabsTrigger value="payroll-components">
            <Coins className="mr-2 h-4 w-4" />
            Payroll Components
          </TabsTrigger>
          <TabsTrigger value="leave-components">
            <CalendarDays className="mr-2 h-4 w-4" />
            Leave Components
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FileText className="mr-2 h-4 w-4" />
            Documents
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Company Info */}
        <TabsContent value="company-info" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Company Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="companyName">Company Name</Label>
                  <Input
                    id="companyName"
                    value={companySettings.name}
                    onChange={(e) =>
                      setCompanySettings({
                        ...companySettings,
                        name: e.target.value,
                      })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="companyEmail">Company Email</Label>
                  <Input
                    id="companyEmail"
                    type="email"
                    value={companySettings.email}
                    onChange={(e) =>
                      setCompanySettings({
                        ...companySettings,
                        email: e.target.value,
                      })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="companyPhone">Phone Number</Label>
                  <Input
                    id="companyPhone"
                    value={companySettings.phone}
                    onChange={(e) =>
                      setCompanySettings({
                        ...companySettings,
                        phone: e.target.value,
                      })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="pan">PAN Number</Label>
                  <Input
                    id="pan"
                    value={companySettings.pan}
                    onChange={(e) =>
                      setCompanySettings({
                        ...companySettings,
                        pan: e.target.value,
                      })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="tan">TAN Number</Label>
                  <Input
                    id="tan"
                    value={companySettings.tan}
                    onChange={(e) =>
                      setCompanySettings({
                        ...companySettings,
                        tan: e.target.value,
                      })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="legalName">Legal Name</Label>
                  <Input
                    id="legalName"
                    value={companySettings.legalName || ""}
                    onChange={(e) =>
                      setCompanySettings({
                        ...companySettings,
                        legalName: e.target.value,
                      })
                    }
                    className="mt-1"
                    placeholder="Legal entity name"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={companySettings.address}
                  onChange={(e) =>
                    setCompanySettings({
                      ...companySettings,
                      address: e.target.value,
                    })
                  }
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={handleSaveCompany}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 2: Manager Info */}
        <TabsContent value="manager-info" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Manager Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {managerLoading ? (
                <div className="flex justify-center items-center py-8 text-gray-500">
                  Loading manager information...
                </div>
              ) : !hasManagerData ? (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-6 text-center">
                  <p className="text-gray-700 font-medium text-lg">
                    No Manager Data Available
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    This company does not currently have a manager assigned.
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="managerName">Manager Name</Label>
                      <Input
                        id="managerName"
                        value={managerSettings.name}
                        onChange={(e) =>
                          setManagerSettings({
                            ...managerSettings,
                            name: e.target.value,
                          })
                        }
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="managerEmail">Manager Email</Label>
                      <Input
                        id="managerEmail"
                        type="email"
                        value={managerSettings.email}
                        onChange={(e) =>
                          setManagerSettings({
                            ...managerSettings,
                            email: e.target.value,
                          })
                        }
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="managerPhone">Manager Phone</Label>
                      <Input
                        id="managerPhone"
                        value={managerSettings.phone}
                        onChange={(e) =>
                          setManagerSettings({
                            ...managerSettings,
                            phone: e.target.value,
                          })
                        }
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="managerDesignation">Designation</Label>
                      <Input
                        id="managerDesignation"
                        value={managerSettings.designation}
                        onChange={(e) =>
                          setManagerSettings({
                            ...managerSettings,
                            designation: e.target.value,
                          })
                        }
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveManager}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Save className="mr-2 h-4 w-4" />
                      Save Changes
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 3: Payroll Components */}
        <TabsContent value="payroll-components" className="mt-6 space-y-5">
          {/* Summary Metric Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
              <p className="text-xs font-medium text-gray-500">
                Total Super List
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {salaryFields.length}
              </p>
              <span className="text-[11px] text-blue-600 font-medium">
                Available Components
              </span>
            </div>
            <div className="p-4 bg-white rounded-xl border border-blue-200 bg-blue-50/30 shadow-sm">
              <p className="text-xs font-medium text-blue-800">
                Assigned to Company
              </p>
              <p className="text-2xl font-bold text-blue-700 mt-1">
                {selectedSalaryFieldIds.length}{" "}
                <span className="text-xs font-normal text-blue-500">
                  / {salaryFields.length}
                </span>
              </p>
              <span className="text-[11px] text-blue-600 font-medium">
                Company #{effectiveOrgId}
              </span>
            </div>
            <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
              <p className="text-xs font-medium text-gray-500">
                Earnings Active
              </p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">
                {
                  salaryFields.filter(
                    (f) =>
                      String(f.fieldType || f.category || "")
                        .toUpperCase()
                        .includes("EARN") &&
                      selectedSalaryFieldIds.includes(f.id),
                  ).length
                }
              </p>
              <span className="text-[11px] text-gray-400">
                of{" "}
                {
                  salaryFields.filter((f) =>
                    String(f.fieldType || f.category || "")
                      .toUpperCase()
                      .includes("EARN"),
                  ).length
                }{" "}
                available
              </span>
            </div>
            <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
              <p className="text-xs font-medium text-gray-500">
                Deductions Active
              </p>
              <p className="text-2xl font-bold text-orange-600 mt-1">
                {
                  salaryFields.filter(
                    (f) =>
                      String(f.fieldType || f.category || "")
                        .toUpperCase()
                        .includes("DEDUCT") &&
                      selectedSalaryFieldIds.includes(f.id),
                  ).length
                }
              </p>
              <span className="text-[11px] text-gray-400">
                of{" "}
                {
                  salaryFields.filter((f) =>
                    String(f.fieldType || f.category || "")
                      .toUpperCase()
                      .includes("DEDUCT"),
                  ).length
                }{" "}
                available
              </span>
            </div>
          </div>

          {/* Components Card */}
          <Card className="shadow-sm border-gray-200">
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-gray-100 pb-5">
              <div>
                <div className="flex items-center space-x-2">
                  <CardTitle className="text-xl font-bold text-gray-900">
                    Company Salary Components
                  </CardTitle>
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                    Company #{effectiveOrgId}
                  </span>
                  {isUsingLiveSalaryData ? (
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5"></span>
                      Live API
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                      Standard Super List
                    </span>
                  )}
                </div>
                {/* <p className="text-sm text-gray-500 mt-1">
                  Select which earnings and deductions apply to this organization. Changes will be saved via{" "}
                  <code className="text-xs bg-gray-100 px-1 py-0.5 rounded text-gray-700 font-mono">
                    api/v1/organizations/{effectiveOrgId}/payroll-components
                  </code>
                </p> */}
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchSalaryFields}
                  disabled={salaryFieldsLoading}
                  className="hover:bg-gray-50"
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-1.5 ${salaryFieldsLoading ? "animate-spin" : ""}`}
                  />
                  {salaryFieldsLoading ? "Fetching..." : "Reload"}
                </Button>

                <Button
                  onClick={handleSavePayrollComponents}
                  disabled={savingSalaryComponents}
                  className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                >
                  {savingSalaryComponents ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-1.5" />
                      Save Components ({selectedSalaryFieldIds.length})
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>

            <CardContent className="pt-6 space-y-4">
              {salaryFieldsError && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <span>{salaryFieldsError}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchSalaryFields}
                    className="text-amber-800 hover:text-amber-900 hover:bg-amber-100 h-7 text-xs font-medium"
                  >
                    Retry API
                  </Button>
                </div>
              )}

              {/* Search, Filter, and Bulk Actions Bar */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search components by name, type, or ID..."
                    value={salarySearchQuery}
                    onChange={(e) => setSalarySearchQuery(e.target.value)}
                    className="pl-9 h-10"
                  />
                </div>

                <div className="flex items-center flex-wrap gap-2">
                  {/* Category Pills */}
                  <div className="flex items-center space-x-1.5 bg-gray-50 p-1 rounded-lg border border-gray-200">
                    {[
                      { key: "ALL", label: `All (${salaryFields.length})` },
                      {
                        key: "EARNING",
                        label: `Earnings (${
                          salaryFields.filter((f) =>
                            String(f.fieldType || f.category || "")
                              .toUpperCase()
                              .includes("EARN"),
                          ).length
                        })`,
                      },
                      {
                        key: "DEDUCTION",
                        label: `Deductions (${
                          salaryFields.filter((f) =>
                            String(f.fieldType || f.category || "")
                              .toUpperCase()
                              .includes("DEDUCT"),
                          ).length
                        })`,
                      },
                      { key: "INCOME_TAX", label: "Income Tax" },
                    ].map((cat) => (
                      <button
                        key={cat.key}
                        type="button"
                        onClick={() => setSalaryCategoryFilter(cat.key)}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                          salaryCategoryFilter === cat.key
                            ? "bg-white text-blue-600 shadow-sm font-semibold"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>

                  {/* Bulk Select/Deselect */}
                  <div className="flex items-center space-x-1 border-l border-gray-200 pl-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSelectAllGlobal}
                      className="text-xs h-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      Select All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDeselectAllGlobal}
                      className="text-xs h-8 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </div>

              {/* Status and Payload Hint Bar */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3.5 py-2.5 bg-blue-50/50 border border-blue-100 rounded-lg text-xs text-blue-900 gap-2">
                <div className="flex items-center space-x-2">
                  <CheckSquare className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span>
                    <strong>{selectedSalaryFieldIds.length}</strong> of{" "}
                    <strong>{salaryFields.length}</strong> components selected
                    for this company.
                  </span>
                </div>
                <div className="text-[11px] text-blue-700 font-mono truncate max-w-md">
                  Payload:{" "}
                  {JSON.stringify({
                    salaryFieldIds: selectedSalaryFieldIds.slice(0, 5),
                  })}
                  {selectedSalaryFieldIds.length > 5 ? "..." : ""}
                </div>
              </div>

              {salaryCategoryFilter === "INCOME_TAX" ? (
                <section
                  className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
                  aria-labelledby="company-income-tax-heading"
                >
                  <div className="max-w-xl space-y-2">
                    <h3 id="company-income-tax-heading" className="text-base font-semibold text-gray-900">
                      Overall company income tax
                    </h3>
                    <p className="text-sm text-gray-500">
                      Enter the total income tax amount for {companySettings.name || `Company #${effectiveOrgId}`}.
                    </p>
                    <Label htmlFor="company-income-tax">Overall tax amount (INR)</Label>
                    <Input
                      id="company-income-tax"
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      placeholder="Enter total company tax"
                      value={companyIncomeTax}
                      onChange={(event) => setCompanyIncomeTax(event.target.value)}
                      className="max-w-sm"
                    />
                    <p className="text-xs text-gray-500">
                      This company-level amount is saved on this device when you save payroll components.
                    </p>
                  </div>
                </section>
              ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/80 hover:bg-gray-50/80">
                      <TableHead className="w-12 text-center">
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={isAllVisibleSelected}
                            onCheckedChange={handleToggleSelectAll}
                            aria-label="Select all visible components"
                          />
                        </div>
                      </TableHead>
                      <TableHead className="w-16 font-semibold text-gray-700">
                        ID
                      </TableHead>
                      <TableHead className="font-semibold text-gray-700">
                        Component Name
                      </TableHead>
                      <TableHead className="w-36 font-semibold text-gray-700">
                        Field Type
                      </TableHead>
                      <TableHead className="w-32 font-semibold text-gray-700">
                        Assignment
                      </TableHead>
                      <TableHead className="w-36 font-semibold text-gray-700">
                        Created At
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salaryFieldsLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center py-12 text-gray-500"
                        >
                          <div className="flex flex-col items-center justify-center space-y-2">
                            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                            <span>Loading salary components...</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : filteredSalaryFields.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center py-12 text-gray-500"
                        >
                          No salary components match your search.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSalaryFields.map((field) => {
                        const isSelected = selectedSalaryFieldIds.includes(
                          field.id,
                        );
                        const isEarning = String(
                          field.fieldType || field.category || "",
                        )
                          .toUpperCase()
                          .includes("EARN");
                        const isDeduction = String(
                          field.fieldType || field.category || "",
                        )
                          .toUpperCase()
                          .includes("DEDUCT");

                        const typeBadgeColor = isEarning
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : isDeduction
                            ? "bg-orange-50 text-orange-700 border-orange-200"
                            : "bg-purple-50 text-purple-700 border-purple-200";

                        const formattedDate = field.createdAt
                          ? new Date(field.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              },
                            )
                          : "2026-06-29";

                        return (
                          <TableRow
                            key={field.id}
                            className={`cursor-pointer transition-colors ${
                              isSelected
                                ? "bg-blue-50/40 hover:bg-blue-50/70"
                                : "hover:bg-gray-50/80"
                            }`}
                            onClick={() => handleToggleComponent(field.id)}
                          >
                            <TableCell
                              className="text-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-center">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() =>
                                    handleToggleComponent(field.id)
                                  }
                                  aria-label={`Select ${field.name}`}
                                />
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-xs font-semibold text-gray-500">
                              #{field.id}
                            </TableCell>
                            <TableCell>
                              <div className="font-semibold text-gray-900">
                                {field.name}
                              </div>
                              {field.description && (
                                <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">
                                  {field.description}
                                </p>
                              )}
                            </TableCell>
                            <TableCell>
                              <span
                                className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border inline-flex items-center ${typeBadgeColor}`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                                    isEarning
                                      ? "bg-emerald-500"
                                      : "bg-orange-500"
                                  }`}
                                ></span>
                                {field.fieldType ||
                                  (isEarning ? "EARNING" : "DEDUCTION")}
                              </span>
                            </TableCell>
                            <TableCell>
                              {isSelected ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                  <Check className="w-3 h-3 mr-1 text-blue-600" />
                                  Assigned
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-normal bg-gray-100 text-gray-400 border border-gray-200">
                                  Excluded
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-gray-500 font-mono">
                              {formattedDate}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
              )}

              {/* Bottom Action Bar */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-3 border-t border-gray-100 gap-3">
                <p className="text-xs text-gray-500">
                  Total of{" "}
                  <strong className="text-gray-900">
                    {selectedSalaryFieldIds.length}
                  </strong>{" "}
                  component(s) will be applied to{" "}
                  <strong className="text-gray-900">
                    {companySettings.name || `Company #${effectiveOrgId}`}
                  </strong>
                  .
                </p>
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={handleSavePayrollComponents}
                    disabled={savingSalaryComponents}
                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                  >
                    {savingSalaryComponents ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />
                        Saving Components...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-1.5" />
                        Save Payroll Components
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 4: Leave Components */}
        <TabsContent value="leave-components" className="mt-6">
          <Card>
            <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>Company Leave Components</CardTitle>
                <p className="mt-1 text-sm text-gray-500">
                  Add as many leave types as your company needs and set each annual allowance in days.
                </p>
              </div>
              <Button onClick={handleAddLeaveComponent} variant="outline">
                <Plus className="h-4 w-4" />
                Add Leave Field
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {leaveComponents.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                  No leave fields yet. Select “Add Leave Field” to create one.
                </div>
              ) : (
                <div className="space-y-3">
                  {leaveComponents.map((component, index) => (
                    <div
                      key={component.id}
                      className="grid grid-cols-1 items-end gap-3 rounded-lg border border-gray-200 p-4 sm:grid-cols-[minmax(0,1fr)_220px_auto]"
                    >
                      <div className="space-y-2">
                        <Label htmlFor={`leave-name-${component.id}`}>
                          Leave field {index + 1} name
                        </Label>
                        <Input
                          id={`leave-name-${component.id}`}
                          value={component.name}
                          onChange={(event) =>
                            handleUpdateLeaveComponent(component.id, "name", event.target.value)
                          }
                          placeholder="e.g. Annual Leave"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`leave-days-${component.id}`}>
                          Annual allowance (days)
                        </Label>
                        <Input
                          id={`leave-days-${component.id}`}
                          type="number"
                          min="0"
                          step="0.5"
                          inputMode="decimal"
                          value={component.days}
                          onChange={(event) =>
                            handleUpdateLeaveComponent(component.id, "days", event.target.value)
                          }
                          placeholder="e.g. 12"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Remove leave field ${index + 1}`}
                        onClick={() =>
                          setLeaveComponents((components) =>
                            components.filter((item) => item.id !== component.id),
                          )
                        }
                      >
                        <Trash2 className="h-4 w-4 text-gray-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-end border-t border-gray-100 pt-4">
                <Button onClick={handleSaveLeaveComponents} disabled={savingLeaveComponents}>
                  <Save className="h-4 w-4" />
                  {savingLeaveComponents ? "Saving..." : "Save Leave Components"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 5: Documents */}
        <TabsContent value="documents" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Documents</CardTitle>
              <div>
                <input
                  type="file"
                  id="document-upload"
                  className="hidden"
                  onChange={handleUploadDocument}
                />
                <label htmlFor="document-upload">
                  <Button asChild variant="outline" className="cursor-pointer">
                    <span>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload Document
                    </span>
                  </Button>
                </label>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-xs">
                        {doc.type}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {doc.name}
                        </h4>
                        <p className="text-sm text-gray-500">
                          Uploaded on {doc.date} • {doc.size}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon">
                      <Download className="h-5 w-5 text-gray-500" />
                    </Button>
                  </div>
                ))}
                {documents.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No documents uploaded yet.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
